import _, {chain, compact, isUndefined, orderBy, partition, sumBy} from 'lodash';
import {DateTime} from 'luxon';
import twttr from 'twitter-text';

import {isElementWithTagName, isTextNode} from '../../../libs/shared/core/src/helpers/browser/domHelpers';
import {isMobile} from '../../../libs/shared/core/src/helpers/environment/mobileEnvHelpers';
import {LocalizeRenderer} from '../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {
  CleanHtml,
  escapeHtml,
  iSolemnlySwearThisHtmlIsClean,
} from '../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {onlyItem} from '../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {
  getRegexpMatches,
  sanitizeFilename,
} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
} from '../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelTypesEnum} from '../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../libs/shared/core/src/models/api/channelModel';
import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {ContactModel} from '../../../libs/shared/core/src/models/api/contactModel';
import {
  DraftMessageModel,
  LoadedMessageModel,
  MessageModel,
  MessageSortPropertiesEnum,
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
  MessageVisualTypesEnum,
  TimelineMessageModel,
  TwitterExtra,
} from '../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../libs/shared/core/src/models/api/recipientModel';
import {isTeammateNamespace} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {truncateHTMLString} from '../../../srcCommon/helpers/htmlTruncator';
import {sanitizeMessageDraftHtml} from '../../../srcCommon/helpers/messageDraftSanitizationHelpers';
import {EmailMessageBodyParts} from '../../../srcCommon/models/messageHelpersTypes';
import {htmlToText} from '../../../srcCommon/services/converters/htmlToTextConverter';
import {RawVariable} from '../../components/common/editor/plugins/text/answerVariablePlugin';
import {ApplicationChannelTypeModel} from '../../models/api/applicationChannelTypeModel';
import {ConversationBaseModel, ConversationModel} from '../../models/api/conversationModel';
import {DataGroupModel} from '../../models/api/dataGroupModel';
import {DraftSummaryModel} from '../../models/api/draftSummaryModel';
import {CellsSortDirectionsEnum} from '../../models/api/sortOptionModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {buildPublicId} from '../../models/applications/publicIdTypes';
import {VariableInterpolator} from '../../services/variableInterpolation/variableInterpolator';
import {ExternalChannelTypeIdsEnum} from '../../settings/inboxes/helpers/channelSettingsHelpers';
import {DataState} from '../../store/data/dataState';
import {InboxesTeammateMessagesParameters} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {isTweetTextValid} from '../twitterHelpers';
import {
  getChannelAttachmentsMaxCount,
  getChannelAttachmentsMaxSize,
  getChannelMessageLengthLimit,
  getChannelRecipientsCountLimit,
  isChannelAffectedByRecipientLimit,
  isChannelSupportedAttachment,
  isChannelValidated,
} from './channelHelpers';
import {buildContactDisplayHandle} from './contactHelpers';
import {sortTrackersForConversation} from './conversationHelpers';
import {findDomainNames, formatRecipientRole, ReplyTypesEnum, VisualRecipientMap} from './recipientHelpers';

/*
 * Sort order.
 */

export type MessageDateGetter = (message: MessageModel) => number;
export function makeMessageDateGetter(parameters: InboxesTeammateMessagesParameters): MessageDateGetter {
  const {sortProperty} = parameters;
  switch (sortProperty) {
    case MessageSortPropertiesEnum.CREATED_AT:
      return (message) => message.createdAt;
    default:
      return (m) => m.sentAt;
  }
}

export function sortMessages(
  parameters: InboxesTeammateMessagesParameters,
  messages: ReadonlyArray<MessageModel>,
) {
  // Find the getter to use to extract a message's date.
  const dateGetter = makeMessageDateGetter(parameters);

  // Find whether to sort in ascending or descending order.
  const {sortDirection} = parameters;
  const isAscending = sortDirection === CellsSortDirectionsEnum.ASCENDING;
  const lodashSortOrder = isAscending ? 'asc' : 'desc';

  // Perform the sort.
  const sortedMessages = orderBy(
    messages,
    [(m) => dateGetter(m), (m) => m.uid],
    [lodashSortOrder, lodashSortOrder],
  );

  return sortedMessages;
}

/*
 * Computed properties.
 */

export function buildMessagePublicId(message: MessageModel) {
  return buildPublicId('message', message.id);
}

export function buildMessagePublicLink(host: string, message: MessageModel) {
  return `${host}/open/${buildMessagePublicId(message)}`;
}

/*
 * Validation.
 */

export function isDraftValid(
  draft: DraftMessageModel,
  channel: ChannelModel,
  company: CompanyModel,
  overrides?: Partial<DraftMessageModel>,
) {
  const sendErrors = findDraftValidationErrors(draft, channel, company, overrides);
  return sendErrors.length === 0;
}

export enum SendDraftErrorsEnum {
  NEEDS_VALIDATION = 'NEEDS_VALIDATION',
  TOO_BIG = 'TOO_BIG',
  TOO_MANY_ATTACHMENTS = 'TOO_MANY_ATTACHMENTS',
  TOO_LONG = 'TOO_LONG',
  TWITTER_TOO_MANY_GIFS = 'TWITTER_TOO_MANY_GIFS', // There is no such thing as too many GIFs.
  TWITTER_MIXED_GIF = 'TWITTER_MIXED_GIF',
  TWITTER_INVALID = 'TWITTER_INVALID',
  BAD_CONTENT_TYPE = 'BAD_CONTENT_TYPE',
  INCOMPLETE = 'INCOMPLETE',
  TOO_MANY_RECIPIENTS = 'TOO_MANY_RECIPIENTS',
  TOO_MANY_RECIPIENTS_TRIAL = 'TOO_MANY_RECIPIENTS_TRIAL',
}

type DraftErrorHandler = (company: CompanyModel, channel: ChannelModel, draft: DraftMessageModel) => void;
type DraftErrors = {[id: string]: DraftErrorHandler};

const draftErrors: DraftErrors = {
  [SendDraftErrorsEnum.NEEDS_VALIDATION]: (_company, channel, _draft) => !isChannelValidated(channel),

  [SendDraftErrorsEnum.TOO_BIG]: (_company, channel, draft) => {
    const maxSize = getChannelAttachmentsMaxSize(channel);
    return Boolean(maxSize && sumBy(draft.attachments, (a) => a.size) > maxSize);
  },

  [SendDraftErrorsEnum.TOO_MANY_ATTACHMENTS]: (_company, channel, draft) => {
    const maxCount = getChannelAttachmentsMaxCount(channel);
    return Boolean(maxCount && draft.attachments.length > maxCount);
  },

  [SendDraftErrorsEnum.TOO_LONG]: (_company, channel, draft) => {
    const maxLength = getChannelMessageLengthLimit(channel);
    const textLength =
      channel.messageType === MessageTypesEnum.TWEET
        ? twttr.parseTweet(draft.text).weightedLength
        : draft.text.length;
    return Boolean(maxLength && textLength > maxLength);
  },

  [SendDraftErrorsEnum.TWITTER_TOO_MANY_GIFS]: (_company, _channel, draft) => {
    const isTweet = draft.type === MessageTypesEnum.TWEET;
    const hasTooManyGifs = draft.attachments.filter((a) => a.contentType === 'image/gif').length > 1;
    return isTweet && hasTooManyGifs;
  },

  [SendDraftErrorsEnum.TWITTER_MIXED_GIF]: (_company, _channel, draft) => {
    if (draft.type !== MessageTypesEnum.TWEET) {
      return false;
    }

    const attachmentTypes = draft.attachments.map((a) => a.contentType);
    const hasGif = attachmentTypes.includes('image/gif');
    const hasImage = attachmentTypes.some((ct) => ct === 'image/jpeg' || ct === 'image/png');

    return hasGif && hasImage;
  },

  [SendDraftErrorsEnum.TWITTER_INVALID]: (_company, channel, draft) => {
    const isTwitterChannel = channel.type === ChannelTypesEnum.TWITTER;
    return isTwitterChannel && draft.text && !isTweetTextValid(draft.text);
  },

  [SendDraftErrorsEnum.BAD_CONTENT_TYPE]: (_company, channel, draft) =>
    draft.attachments.some((a) => !isChannelSupportedAttachment(channel, a)),

  [SendDraftErrorsEnum.INCOMPLETE]: (_company, _channel, draft) => {
    const recipients = draft.recipients.filter((r) => r.role !== RecipientRolesEnum.FROM);

    if (draft.visualType === MessageVisualTypesEnum.CHAT) {
      return Boolean(recipients.length === 0 || (!draft.text && draft.attachments.length === 0));
    }

    // Tweets don't require a recipient.
    if (draft.type === MessageTypesEnum.TWEET) {
      return !draft.text;
    }

    return recipients.length === 0;
  },

  [SendDraftErrorsEnum.TOO_MANY_RECIPIENTS]: (_company, channel, draft) => {
    const recipientsCount = getReceivingRecipientsCount(draft);
    const recipientsCountLimit = getChannelRecipientsCountLimit(channel);
    return recipientsCount > recipientsCountLimit;
  },

  [SendDraftErrorsEnum.TOO_MANY_RECIPIENTS_TRIAL]: (company, channel, draft) => {
    const {isInTrial, preferences} = company;
    const {shouldLimitRecipientsDuringTrial} = preferences;
    const useRecipientLimit = isChannelAffectedByRecipientLimit(channel);
    const recipientsCount = getReceivingRecipientsCount(draft);

    return isInTrial && useRecipientLimit && recipientsCount > 10 && shouldLimitRecipientsDuringTrial;
  },
};

export function findDraftValidationErrors(
  draft: DraftMessageModel,
  channel: ChannelModel,
  company: CompanyModel,
  overrides?: Partial<DraftMessageModel>,
) {
  const fullDraft = {...draft, ...overrides};

  return Object.keys(draftErrors).filter((errorType) => {
    const handler = draftErrors[errorType];
    return handler(company, channel, fullDraft);
  }) as ReadonlyArray<SendDraftErrorsEnum>;
}

/*
 * Send warnings.
 */

export enum MessageWarningTypesEnum {
  NO_SUBJECT = 'NO_SUBJECT',
  NO_ATTACHMENTS = 'NO_ATTACHMENTS',
  MANY_RECIPIENTS = 'MANY_RECIPIENTS',
  FAILED_ATTACHMENTS = 'FAILED_ATTACHMENTS',
  UPLOADING_ATTACHMENTS = 'UPLOADING_ATTACHMENTS',
  HAS_RAW_VARIABLES = 'HAS_RAW_VARIABLES',
}

interface DefaultMessageWarning {
  type:
    | MessageWarningTypesEnum.NO_SUBJECT
    | MessageWarningTypesEnum.NO_ATTACHMENTS
    | MessageWarningTypesEnum.FAILED_ATTACHMENTS
    | MessageWarningTypesEnum.UPLOADING_ATTACHMENTS;
}

interface ManyRecipientsMessageWarning {
  type: MessageWarningTypesEnum.MANY_RECIPIENTS;
  domainNames: ReadonlyArray<string>;
  recipientCount: number;
}

interface HasRawVariablesMessageWarning {
  type: MessageWarningTypesEnum.HAS_RAW_VARIABLES;
  rawVariables: readonly RawVariable[];
}

export type MessageWarning =
  | DefaultMessageWarning
  | ManyRecipientsMessageWarning
  | HasRawVariablesMessageWarning;

const attachmentHints = [
  'see attach',
  'is attach',
  'see the attach',
  'find attach',
  'find the attach',
  'i have attach',
  "i've attach",
  'check the attach',
  'attached is',
  'document attach',
  'file attach',
  'pi[e\u00e8]ces?[-\\s]jointes?',
  'vedlegg',
  'vedlagt',
  'vedlegget',
  'vedleggene',
  'legger ved',
];

// Force a confirmation to prevent from accidentally emailing a group.
const manyRecipientsLimit = 10;

export function findSendWarnings(
  draft: DraftMessageModel,
  recipients: VisualRecipientMap,
  attachments: ReadonlyArray<AttachmentModel>,
  shouldWarnManyRecipients: boolean,
  shouldWarnMissingAttachments: boolean,
  rawVariables: readonly RawVariable[],
): ReadonlyArray<MessageWarning> {
  // No warnings for non-email drafts.
  if (draft.type !== MessageTypesEnum.EMAIL) {
    return [];
  }

  const warnings: Array<MessageWarning> = [];

  // 0. Raw (unfilled) variables?
  if (rawVariables.length && !isMobile()) {
    warnings.push({type: MessageWarningTypesEnum.HAS_RAW_VARIABLES, rawVariables});
  }

  // 1. No subject?
  if (!draft.subject || /^\s+$/.test(draft.subject)) {
    warnings.push({type: MessageWarningTypesEnum.NO_SUBJECT});
  }

  // 2. Body mentions attachments but none was provided?
  const draftBody = draft.html || draft.text;

  const {signatureHtml, quoteHtml} = draft;

  // Checking for attachments on the draft, ignoring anything in the signatureHtml or the
  // quoteHtml (which is the message we are replying to).
  const hasAttachmentForDraft = draft.attachments.some(
    (attachment) => !signatureHtml?.includes(attachment.uid) && !quoteHtml?.includes(attachment.uid),
  );

  const attachmentHintsRegexp = new RegExp(attachmentHints.join('|'), 'gi');
  const integrationsAttachmentsRegexp = /dropbox\.com\/s\/|drive\.google\.com\/file\/d\//g;
  const hasAttachments = hasAttachmentForDraft || integrationsAttachmentsRegexp.test(draftBody);

  const shouldHaveAttachments = attachmentHintsRegexp.test(draftBody);

  if (shouldWarnMissingAttachments && shouldHaveAttachments && !hasAttachments) {
    warnings.push({type: MessageWarningTypesEnum.NO_ATTACHMENTS});
  }

  // 3. Many recipients? confirm so you don't accidentally email a group.
  // it's not a warning, just a confirmation.
  const receivingRecipientsCount = getReceivingRecipientsCount(draft);

  if (receivingRecipientsCount >= manyRecipientsLimit && shouldWarnManyRecipients) {
    warnings.push({
      type: MessageWarningTypesEnum.MANY_RECIPIENTS,
      domainNames: findDomainNames(recipients),
      recipientCount: receivingRecipientsCount,
    });
  }

  // 4. Are there failed attachments still present?
  if (attachments.some((a) => a.status === AttachmentStatusesEnum.DRAFT && a.hasFailed)) {
    warnings.push({type: MessageWarningTypesEnum.FAILED_ATTACHMENTS});
  }

  // 5. If the user attempts to send while attachments are uploading, show a warning.
  if (isMobile() && attachments.some((a) => a.status === AttachmentStatusesEnum.DRAFT && !a.hasFailed)) {
    warnings.push({type: MessageWarningTypesEnum.UPLOADING_ATTACHMENTS});
  }

  return warnings;
}

/*
 * Reply.
 */

const quoteMaxLength = 5000000;

export function buildReplyQuote(
  localize: Localizer,
  replyTo: LoadedMessageModel,
  fromRecipient: VisualRecipientModel,
) {
  const header = formatReplyHeader(localize, replyTo, fromRecipient);
  return formatReplyQuote(header, replyTo);
}

export function buildReplyQuoteWithHeaders(
  localize: Localizer,
  replyTo: LoadedMessageModel,
  allRecipients: ReadonlyArray<VisualRecipientModel>,
) {
  // For legal reasons, some customers (SSY) need to repeat the headers of the replied message when replying.
  const header = formatForwardHeader(localize, replyTo, allRecipients, false);

  return formatReplyQuote(header, replyTo);
}

function formatReplyQuote(header: CleanHtml, replyTo: LoadedMessageModel): CleanHtml {
  // Body.
  const canUseFullBody = replyTo.body && replyTo.body.length < quoteMaxLength;
  const body = canUseFullBody ? replyTo.body : replyTo.lightBody || replyTo.body;
  const cleanWrappedBody = sanitizeMessageDraftHtml(body || '', replyTo.id);

  return iSolemnlySwearThisHtmlIsClean(`${header}<br><br>${cleanWrappedBody}`);
}

function formatReplyHeader(
  localize: Localizer,
  replyTo: LoadedMessageModel,
  recipient: VisualRecipientModel,
): CleanHtml {
  const dateString = localize(DateTime.fromMillis(replyTo.sentAt), DateTime.DATETIME_FULL);
  return escapeHtml(
    localize(
      defineMessage({
        id: 'quote-reply-header',
        message: `On ${dateString} ${buildContactDisplayHandle(recipient)} wrote:`,
      }),
    ),
  );
}

/*
 * Forward.
 */

export function buildForwardQuote(
  localize: Localizer,
  replyTo: LoadedMessageModel,
  recipients: ReadonlyArray<VisualRecipientModel>,
): CleanHtml {
  // Header.
  const header: CleanHtml = formatForwardHeader(localize, replyTo, recipients, true);

  // Body.
  const body =
    replyTo.body && replyTo.body.length < quoteMaxLength ? replyTo.body : replyTo.lightBody || replyTo.body;
  const cleanWrappedBody = sanitizeMessageDraftHtml(body || '', replyTo.id);

  return iSolemnlySwearThisHtmlIsClean(`${header}<br><br>${cleanWrappedBody}`);
}

const destinationRoles = [RecipientRolesEnum.TO, RecipientRolesEnum.CC, RecipientRolesEnum.BCC];
export function isForwardingToTeammate(
  contacts: ResourceMap<ContactModel>,
  mentionableEmails: ReadonlySet<string>,
  draft: DraftMessageModel,
) {
  return (
    draft.type === MessageTypesEnum.EMAIL &&
    draft.forwardHtml &&
    draft.recipients.some((recipient) => {
      if (!destinationRoles.includes(recipient.role)) {
        return false;
      }

      const contact = contacts[recipient.contactUid];
      return Boolean(contact && mentionableEmails.has(contact.handle));
    })
  );
}

export function isMassSend(draft: DraftMessageModel) {
  const toRecipients = draft.recipients.filter((r) => r.role === RecipientRolesEnum.TO);
  return draft.type === MessageTypesEnum.SMS && toRecipients.length >= 2;
}

function formatForwardHeader(
  localize: Localizer,
  replyTo: LoadedMessageModel,
  recipients: ReadonlyArray<VisualRecipientModel>,
  shouldIncludeSeparator: boolean,
): CleanHtml {
  // Recipients.
  const toRecipients = recipients.filter((r) => r.role === RecipientRolesEnum.TO);
  const ccRecipients = recipients.filter((r) => r.role === RecipientRolesEnum.CC);
  const fromRecipients = recipients.filter((r) => r.role === RecipientRolesEnum.FROM);

  // Header.
  const headerTitle = localize(
    defineMessage({
      id: 'quote-forward-title',
      message: 'Forwarded message',
    }),
  );

  // Date.
  const dateString = localize(DateTime.fromMillis(replyTo.sentAt), DateTime.DATETIME_FULL);

  // Fields.
  const fields = compact([
    shouldIncludeSeparator ? `----------- ${headerTitle} -----------` : undefined,
    formatRecipients(localize, RecipientRolesEnum.FROM, fromRecipients),
    localize(
      defineMessage({
        id: 'quote-forward-date',
        message: `Date: ${dateString}`,
      }),
    ),
    localize(
      defineMessage({
        id: 'quote-forward-subject',
        message: `Subject: ${cleanSubject(replyTo.subject)}`,
      }),
    ),
    toRecipients && formatRecipients(localize, RecipientRolesEnum.TO, toRecipients),
    ccRecipients && formatRecipients(localize, RecipientRolesEnum.CC, ccRecipients),
  ]);

  return iSolemnlySwearThisHtmlIsClean(fields.map(escapeHtml).join('<br>'));
}

/*
 * Subject.
 */

type SubjectPrefixes = {[T in ReplyTypesEnum]: LocalizeRenderer};
const subjectPrefixes: SubjectPrefixes = {
  [ReplyTypesEnum.SINGLE]: (localize) =>
    localize(
      defineMessage({
        id: 'subject-prefix-reply',
        message: 'Re',
      }),
    ),
  [ReplyTypesEnum.ALL]: (localize) =>
    localize(
      defineMessage({
        id: 'subject-prefix-reply',
        message: 'Re',
      }),
    ),
  [ReplyTypesEnum.INTRO]: (localize) =>
    localize(
      defineMessage({
        id: 'subject-prefix-reply',
        message: 'Re',
      }),
    ),
  [ReplyTypesEnum.FORWARD]: (localize) =>
    localize(
      defineMessage({
        id: 'subject-prefix-forward',
        message: 'Fw',
      }),
    ),
};

export function buildReplySubject(localize: Localizer, replyType: ReplyTypesEnum, subject?: string) {
  return `${subjectPrefixes[replyType](localize)}: ${cleanSubject(subject)}`;
}

const subjectPrefixRegexp = /^(?:re:\s*|fwd:\s*|fw:\s*)+/i;
/** Remove all previous occurrences of subject prefixes. */
function cleanSubject(subject = '') {
  return subject.replace(subjectPrefixRegexp, '');
}

/*
 * Split.
 */

const splittableMessageTypes = [
  MessageTypesEnum.EMAIL,
  MessageTypesEnum.FACEBOOK,
  MessageTypesEnum.FRONT_CHAT,
  MessageTypesEnum.INTERCOM,
  MessageTypesEnum.LAYER,
  MessageTypesEnum.SMOOCH,
  MessageTypesEnum.SMS,
  MessageTypesEnum.WHATSAPP,
  MessageTypesEnum.YALO_WHATSAPP,
];

const splittableMessageStatus = [MessageStatusesEnum.IMPORTED, MessageStatusesEnum.SENT];

export function isSplitSupportedForMessageType(conversation: ConversationModel) {
  return splittableMessageTypes.includes(conversation.messageType);
}

function isSplitSupportedForChannelType(applicationChannelType: ApplicationChannelTypeModel) {
  switch (applicationChannelType.uid) {
    case ExternalChannelTypeIdsEnum.TELEGRAM.toString():
    case ExternalChannelTypeIdsEnum.WHATSAPP:
      return true;
    default:
      return false;
  }
}

export function canSplitMessage(
  message: MessageModel,
  conversation: ConversationModel,
  applicationChannelType?: ApplicationChannelTypeModel,
) {
  const correctMessageType =
    applicationChannelType && conversation.messageType === MessageTypesEnum.CUSTOM
      ? isSplitSupportedForChannelType(applicationChannelType)
      : isSplitSupportedForMessageType(conversation);
  const correctNumMessages = conversation.numMessages > 1;
  const correctMessageStatus = splittableMessageStatus.includes(message.status);
  const hasDraft = Boolean(conversation.lastDraft);

  return (
    correctMessageType && correctNumMessages && correctMessageStatus && !hasDraft && !message.isFirstMessage
  );
}

/*
 * Helpers.
 */

const draftStatuses = new Set([
  MessageStatusesEnum.DRAFT_COMPOSE,
  MessageStatusesEnum.DRAFT_PENDING,
  MessageStatusesEnum.DRAFT_SENDING,
  MessageStatusesEnum.DRAFT_LATER,
  MessageStatusesEnum.DRAFT_FAILED,
]);

/** Whether the given message is a partial draft or a loaded draft. */
export function isDraftMessage(message: MessageModel): boolean {
  return (
    message.visualStatus === MessageVisualStatusesEnum.DRAFT ||
    (message.visualStatus === MessageVisualStatusesEnum.PARTIAL && draftStatuses.has(message.status))
  );
}

/** Whether the given message is a new draft that has never been changed or saved. */
export function isUntouchedDraftMessage(message: MessageModel): boolean {
  return message.visualStatus === MessageVisualStatusesEnum.DRAFT && !message.wasSaved && !message.isSaving;
}

/** Whether the given message is a draft with client-side changes. */
export function isDirtyDraftMessage(message: MessageModel): boolean {
  return message.visualStatus === MessageVisualStatusesEnum.DRAFT && message.isSaving;
}

/**
 * Checks if the message is the first one in a new conversation, which can happen through
 * the "Forward message as new conversation" dropdown action.
 */
export function isDraftInNewConversation(message: DraftMessageModel) {
  // There seem to be multiple ways to derive this value, such as from `conversationId` and
  // `conversationUrl`, but it seems `inReplyToId` is the most reliable because the
  // `conversationId` and `conversationUrl` fields seem to be populated as a result of many
  // different actions which create a new conversation for the current draft.
  //
  // If there's no `inReplyToId`, then it's a new conversation, so return true.
  return !message.inReplyToId;
}

/** Whether the given message is known on the server (has ever been saved to the server). */
export function isServerMessage(message: MessageModel): boolean {
  return message.visualStatus !== MessageVisualStatusesEnum.DRAFT || message.wasSaved;
}

function formatRecipients(
  localize: Localizer,
  role: RecipientRolesEnum,
  recipients: ReadonlyArray<VisualRecipientModel>,
) {
  if (recipients.length === 0) {
    return '';
  }

  const localRole = formatRecipientRole(localize, role);
  return `${localRole}: ${recipients.map(buildContactDisplayHandle).join(', ')}`;
}

export function findVisibleAttachments(message: MessageModel) {
  return chain(message.attachments)
    .filter((a) => !a.isInline)
    .sortBy((a) => a.attachedAt ?? 0)
    .value();
}

// eslint-disable-next-line no-useless-escape
const inlineAttachmentRegexp = /<img([^>]+)front-cid=["']?([^"'\s\/>]+)["']?([^>]*)\/?>/g;
// eslint-disable-next-line no-useless-escape
const inlineAttachmentUrlRegexp = /\bsrc=["']?([^"'\s]+\/attachments\/(\w{32,})[^"'\s]*)["']?/;
// eslint-disable-next-line no-useless-escape
const inlineAttachmentNameRegexp = /\bfront-name=(?:["']([^"']+)["']|([^\s]+))/;

export interface InlineAttachmentInfo {
  cid: string;
  url?: string;
  uid?: string;
  name?: string;
}

export function findInlineAttachmentsInHtml(html = ''): ReadonlyArray<InlineAttachmentInfo> {
  const matches = getRegexpMatches(inlineAttachmentRegexp, html);
  return matches.map((match) => {
    const cid = match[2];

    const otherAttributes = `${match[1]} ${match[3]}`;
    const urlMatch = otherAttributes.match(inlineAttachmentUrlRegexp);
    const url = urlMatch ? urlMatch[1] : undefined;
    const uid = urlMatch ? urlMatch[2] : undefined;
    const nameMatch = otherAttributes.match(inlineAttachmentNameRegexp);
    const name = nameMatch ? nameMatch[1] || nameMatch[2] : undefined;

    return {cid, url, uid, name};
  });
}

export function findDraftMessageData(data: DataState, messageUid: string) {
  const message = data.messages[messageUid];
  if (!message || message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
    return {};
  }

  const channel = data.channels.byId[message.fromChannelId];
  const conversation = data.conversations.byId[message.conversationId];

  return {message, channel, conversation};
}

/*
 * Parse and deconstruct the body of a email timeline message.
 */

export function parseEmailMessageBody(body: string, lightBody?: string): EmailMessageBodyParts {
  const document = new DOMParser().parseFromString(body, 'text/html');
  const container = document.body;

  let frontSignature: HTMLElement | undefined;
  let frontSeen: HTMLImageElement | undefined;
  let frontReplyQuote: HTMLElement | undefined;
  let frontForwardHeader: Text | undefined;

  // eslint-disable-next-line no-bitwise
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    // Look for a Front-generated signature.
    if (!frontSignature && isElementWithTagName('div', node) && node.classList.contains('front-signature')) {
      frontSignature = node;
    }

    // Look for a Front-generated tracking pixel.
    if (
      !frontSeen &&
      isElementWithTagName('img', node) &&
      node.src.includes('/seen/') &&
      node.width === 1 &&
      node.height === 1
    ) {
      frontSeen = node;
    }

    if (isElementWithTagName('blockquote', node) && node.classList.contains('front-blockquote')) {
      // Look for a Front-generated reply quote.
      frontReplyQuote = node;

      // Stop looking for anything else.
      break;
    }

    // Look for a Front-generated forward quote.
    if (
      isTextNode(node) &&
      node.data.match(/^----------- .+ -----------$/) &&
      isElementWithTagName('br', node.previousSibling)
    ) {
      frontForwardHeader = node;

      // Stop looking for anything else.
      break;
    }

    node = walker.nextNode();
  }

  // Remove the signature if found.
  if (frontSignature && frontSignature.parentNode) {
    removePreviousBreakSibling(frontSignature);
    frontSignature.parentNode.removeChild(frontSignature);
  }

  // Remove the tracking pixel if found.
  if (frontSeen && frontSeen.parentNode) {
    frontSeen.parentNode.removeChild(frontSeen);
  }

  // Handle the reply quote if found.
  if (frontReplyQuote && frontReplyQuote.parentNode) {
    removePreviousBreakSibling(frontReplyQuote);
    frontReplyQuote.parentNode.removeChild(frontReplyQuote);

    return {
      bodyHtml: iSolemnlySwearThisHtmlIsClean(container.innerHTML),
      quoteHtml: iSolemnlySwearThisHtmlIsClean(frontReplyQuote.innerHTML),
    };
  }

  // Handle the forward quote if found.
  if (frontForwardHeader) {
    removePreviousBreakSibling(frontForwardHeader);

    // Transplant all subsequent siblings to a new container.
    const forwardContainer = document.createElement('div');
    let childNode: ChildNode | null = frontForwardHeader;
    while (childNode) {
      const nextChildNode: ChildNode | null = childNode.nextSibling;
      forwardContainer.appendChild(childNode);
      childNode = nextChildNode;
    }

    return {
      bodyHtml: iSolemnlySwearThisHtmlIsClean(container.innerHTML),
      forwardHtml: iSolemnlySwearThisHtmlIsClean(forwardContainer.innerHTML),
    };
  }

  // We didn't find a Front-generated quote, so use the lightBody. We cannot easily/safely generate a quote.
  if (body && lightBody) {
    return parseEmailMessageBody(lightBody);
  }

  // No quotes were found, but we may have extracted a signature.
  return {
    bodyHtml: iSolemnlySwearThisHtmlIsClean(container.innerHTML),
  };
}

function removePreviousBreakSibling(node: ChildNode) {
  const {parentNode, previousSibling} = node;
  if (!parentNode || !isElementWithTagName('br', previousSibling)) {
    return;
  }

  parentNode.removeChild(previousSibling);
}

function getReceivingRecipientsCount(draft: DraftMessageModel) {
  const sendingRecipientRoles = [RecipientRolesEnum.FROM, RecipientRolesEnum.REPLY_TO];
  const receivingRecipients = draft.recipients.filter((r) => !sendingRecipientRoles.includes(r.role));
  const receivingRecipientsCount = receivingRecipients.length;
  return receivingRecipientsCount;
}

export function draftMessageHasRawVariables(message: DraftMessageModel) {
  return (
    VariableInterpolator.hasRawVariables(message.html) ||
    VariableInterpolator.hasRawVariables(message.subject)
  );
}

/*
 * Draft summary.
 */

export function toDraftSummary(
  message: MessageModel,
  conversation: ConversationBaseModel | undefined,
  hasAutoShareDrafts: boolean,
  userId: number,
): DraftSummaryModel | undefined {
  if (!isDraftMessage(message)) {
    return undefined;
  }

  const blurb = buildMessageBlurb(message);
  const isShared = isMessageVisuallyShared(message, conversation, hasAutoShareDrafts, userId);

  return {
    id: message.id,
    authorId: message.authorId,
    status: message.status,
    type: message.type,
    hasAttachments: message.attachments.length > 0,
    isShared,
    blurb,
    subject: message.subject,
    updatedAt: message.updatedAt,
  };
}

function buildMessageBlurb(message: MessageModel) {
  if (message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
    return message.blurb;
  }

  const blurbCharacterLimit = 200;
  if (message.text) {
    return message.text.substring(0, blurbCharacterLimit);
  }

  const truncatedHtml = truncateHTMLString(message.html, blurbCharacterLimit).result;
  return htmlToText(truncatedHtml);
}

/*
 * Twitter.
 */

export interface TwitterMessageWithExtraModel extends TimelineMessageModel {
  type: MessageTypesEnum.TWEET;
  twitterExtra: TwitterExtra;
}

export function isTwitterMessageWithExtra(
  message: MessageModel | undefined,
): message is TwitterMessageWithExtraModel {
  return Boolean(
    message &&
      message.visualStatus === MessageVisualStatusesEnum.TIMELINE &&
      message.type === MessageTypesEnum.TWEET &&
      message.twitterExtra,
  );
}

/*
 * Data groups.
 */

/** Returns one data group containing only the given message, and another data group containing the rest. */
export function partitionDataGroupByMessageUid(
  messageUid: string | undefined,
  dataGroup: DataGroupModel,
): [DataGroupModel, DataGroupModel] {
  if (isUndefined(messageUid) || !dataGroup.messages) {
    return [{}, dataGroup];
  }

  const [matchingMessages, otherMessages] = partition(
    dataGroup.messages,
    (message) => message.uid === messageUid,
  );
  return [
    {
      messages: matchingMessages,
    },
    {
      ...dataGroup,
      messages: otherMessages,
    },
  ];
}

export function emlFileName(message: MessageModel, backupSubject?: string) {
  const name = message.subject || backupSubject || message.blurb || `message-${message.id}`;
  return `${sanitizeFilename(name)}.eml`;
}

export function isDraftSummaryVisuallyShared(
  draft: DraftSummaryModel,
  conversation: ConversationModel | undefined,
  hasAutoShareDrafts: boolean,
  userId: number,
) {
  // If we don't have the auto share FF, we rely on `.isShared` on the message.
  if (!hasAutoShareDrafts) {
    return draft.isShared;
  }

  if (!conversation) {
    return false;
  }

  const trackers = _(sortTrackersForConversation(conversation))
    .uniqBy((tracker) => tracker.teammateId)
    .value();

  // If the conversation is NOT in a team namespace (e.g it's private) and the only tracker is the current
  // user and the only draft author is the current user (the draft was not created/updated by a delegation),
  // then the draft is private.
  if (
    isTeammateNamespace(conversation.namespace) &&
    onlyItem(trackers)?.teammateId === userId &&
    draft.authorId === userId
  ) {
    return false;
  }

  return draft.isShared;
}

export function isMessageVisuallyShared(
  message: MessageModel,
  conversation: ConversationBaseModel | undefined,
  hasAutoShareDrafts: boolean,
  userId: number,
) {
  // If the message isn't a draft, it can't be shared in the first place.
  if (message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
    return false;
  }

  // If we don't have the auto share FF, we rely on `.isShared` on the message.
  if (!hasAutoShareDrafts) {
    return message.isShared;
  }

  // If the draft isn't in a conversation yet, it's not shared.
  if (!conversation) {
    return false;
  }

  const trackers = _(sortTrackersForConversation(conversation))
    .uniqBy((tracker) => tracker.teammateId)
    .value();

  // If the conversation is NOT in a team namespace (e.g it's private) and the only tracker is the current
  // user and the only draft author is the current user (the draft was not created/updated by a delegation),
  // then the draft is private.
  if (
    isTeammateNamespace(conversation.namespace) &&
    onlyItem(trackers)?.teammateId === userId &&
    onlyItem(message.authorIds ?? [message.authorId]) === userId
  ) {
    return false;
  }
  return message.isShared;
}

// We want to render a warning for messages that were delivered late,
// but only if we are reasonably close to when they were sent.
// e.g no point in warning that a 2-month old email was delivered 5 mins late.
export function computeRenderDelayedUpstreamWarning(message: MessageModel) {
  if (!message.wasDelayedUpstream) {
    return false;
  }

  const delay = message.createdAt - message.sentAt;
  const sinceSent = Date.now() - message.sentAt;

  return sinceSent < 10 * delay;
}
